/**
 * Offline Task Queue — Matches Xamarin MiddlewareService offline patterns
 *
 * Stores pending task update requests (start/complete) in localStorage when offline.
 * On reconnect, the queue is flushed: all pending items are sent in a single batch
 * UpdateTask API call, matching Xamarin PushDataToServer().
 *
 * Xamarin keys mirrored:
 * - OfflineUpdatedTaskList → STORAGE_KEYS.offlineUpdatedTaskList
 * - OfflineMyTaskList      → STORAGE_KEYS.offlineMyTaskList
 *
 * Constraints (matching Xamarin):
 * - Only START and COMPLETE actions are queued offline.
 * - CANCEL and ACCEPT are blocked offline (show "No Internet" toast).
 */

import { STORAGE_KEYS } from '@/constants';

import type { TaskDetail, UpdateTaskDetail } from '@/types';

// ─────────────────────────────────────────────────────────────────
// Queue — pending update requests
// ─────────────────────────────────────────────────────────────────

/**
 * Get all pending offline task updates.
 * Matches Xamarin: GetOfflineUpdatedTaskListAsync()
 */
export function getOfflineUpdatedTaskList(): UpdateTaskDetail[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.offlineUpdatedTaskList);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as UpdateTaskDetail[]) : [];
  } catch {
    return [];
  }
}

/**
 * Append a task update to the offline queue.
 * Matches Xamarin: StoreOfflineUpdatedTaskAsync()
 */
export function addToOfflineQueue(item: UpdateTaskDetail): void {
  const current = getOfflineUpdatedTaskList();
  current.push(item);
  localStorage.setItem(STORAGE_KEYS.offlineUpdatedTaskList, JSON.stringify(current));
}

/**
 * Clear the offline queue after successful sync.
 * Matches Xamarin: ClearAllDataAsync(OfflineUpdatedTaskList)
 */
export function clearOfflineQueue(): void {
  localStorage.removeItem(STORAGE_KEYS.offlineUpdatedTaskList);
}

/**
 * Replace the offline queue with the given items.
 * Used after partial sync to keep only unprocessed items.
 */
export function setOfflineQueue(items: UpdateTaskDetail[]): void {
  if (items.length === 0) {
    localStorage.removeItem(STORAGE_KEYS.offlineUpdatedTaskList);
  } else {
    localStorage.setItem(STORAGE_KEYS.offlineUpdatedTaskList, JSON.stringify(items));
  }
}

/**
 * Check if there are pending offline tasks.
 */
export function hasOfflinePendingTasks(): boolean {
  return getOfflineUpdatedTaskList().length > 0;
}

export function setInProgressTaskStorage(task: TaskDetail): void {
  localStorage.setItem(STORAGE_KEYS.inProgressTask, JSON.stringify(task));
}

export function getInProgressTaskStorage(): TaskDetail | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.inProgressTask);
    if (!raw) return null;
    return JSON.parse(raw) as TaskDetail;
  } catch {
    return null;
  }
}

export function clearInProgressTaskStorage(): void {
  localStorage.removeItem(STORAGE_KEYS.inProgressTask);
}
